import React, { useState } from 'react';
import travelApiService from '../utils/travelApiService';
import './ShareLinkModal.css';

const ShareLinkModal = ({ planId, onClose, onSuccess }) => {
  const [permission, setPermission] = useState('view');
  const [enableExpiry, setEnableExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryTime, setExpiryTime] = useState('23:59');
  const [enableMaxUses, setEnableMaxUses] = useState(false);
  const [maxUses, setMaxUses] = useState(5);
  const [isCreating, setIsCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [error, setError] = useState(null);

  const permissionLabels = {
    view: {
      title: '읽기 전용',
      description: '친구가 여행 계획을 볼 수만 있습니다',
      icon: '👁️',
    },
    edit: {
      title: '편집 가능',
      description: '친구가 일정을 수정할 수 있습니다',
      icon: '✏️',
    },
    admin: {
      title: '관리자',
      description: '친구가 삭제 및 공유 관리도 가능합니다',
      icon: '⚙️',
    },
  };

  const handleCreateLink = async () => {
    setError(null);
    setIsCreating(true);

    try {
      // 만료 시간을 Unix timestamp로 변환
      let expiresAt = null;
      if (enableExpiry && expiryDate) {
        const dateTime = new Date(`${expiryDate}T${expiryTime}`);
        expiresAt = Math.floor(dateTime.getTime() / 1000);
      }

      const shareData = {
        permission,
        ...(expiresAt && { expires_at: expiresAt }),
        ...(enableMaxUses && { max_uses: parseInt(maxUses) || 5 }),
      };

      const response = await travelApiService.createShareLink(planId, shareData);

      if (response.success && response.data) {
        const shareUrl = `${window.location.origin}/share/${response.data.token}`;
        setCreatedLink({
          ...response.data,
          shareUrl,
        });

        // 클립보드에 복사
        try {
          await navigator.clipboard.writeText(shareUrl);
          alert('✅ 공유 링크가 생성되고 클립보드에 복사되었습니다!');
        } catch (clipboardError) {
          console.error('클립보드 복사 실패:', clipboardError);
          alert('✅ 공유 링크가 생성되었습니다!');
        }

        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        throw new Error(response.message || '공유 링크 생성에 실패했습니다');
      }
    } catch (error) {
      console.error('공유 링크 생성 오류:', error);

      if (error.response?.status === 403) {
        setError('권한이 없습니다. 여행 계획의 소유자만 공유 링크를 생성할 수 있습니다.');
      } else if (error.response?.status === 404) {
        setError('여행 계획을 찾을 수 없습니다.');
      } else {
        setError(error.message || '공유 링크 생성에 실패했습니다.');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!createdLink) return;

    try {
      await navigator.clipboard.writeText(createdLink.shareUrl);
      alert('✅ 링크가 클립보드에 복사되었습니다!');
    } catch (error) {
      console.error('클립보드 복사 실패:', error);
      alert('❌ 클립보드 복사에 실패했습니다.');
    }
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return '만료 없음';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content share-link-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔗 공유 링크 생성</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {!createdLink ? (
            <>
              {/* 권한 선택 */}
              <div className="form-section">
                <label className="form-label">권한 선택</label>
                <div className="permission-options">
                  {Object.entries(permissionLabels).map(([key, label]) => (
                    <label key={key} className={`permission-option ${permission === key ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="permission"
                        value={key}
                        checked={permission === key}
                        onChange={(e) => setPermission(e.target.value)}
                      />
                      <div className="permission-content">
                        <div className="permission-icon">{label.icon}</div>
                        <div className="permission-text">
                          <div className="permission-title">{label.title}</div>
                          <div className="permission-description">{label.description}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* 만료 설정 */}
              <div className="form-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={enableExpiry}
                    onChange={(e) => setEnableExpiry(e.target.checked)}
                  />
                  <span>만료 시간 설정</span>
                </label>

                {enableExpiry && (
                  <div className="expiry-inputs">
                    <input
                      type="date"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="form-input"
                    />
                    <input
                      type="time"
                      value={expiryTime}
                      onChange={(e) => setExpiryTime(e.target.value)}
                      className="form-input"
                    />
                  </div>
                )}
              </div>

              {/* 사용 제한 */}
              <div className="form-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={enableMaxUses}
                    onChange={(e) => setEnableMaxUses(e.target.checked)}
                  />
                  <span>최대 사용 횟수 제한</span>
                </label>

                {enableMaxUses && (
                  <input
                    type="number"
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    min="1"
                    max="100"
                    className="form-input max-uses-input"
                  />
                )}
              </div>

              {/* 에러 메시지 */}
              {error && (
                <div className="error-message">
                  ❌ {error}
                </div>
              )}

              {/* 생성 버튼 */}
              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={onClose}
                  disabled={isCreating}
                >
                  취소
                </button>
                <button
                  className="btn-primary"
                  onClick={handleCreateLink}
                  disabled={isCreating}
                >
                  {isCreating ? '생성 중...' : '링크 생성'}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* 생성된 링크 표시 */}
              <div className="created-link-section">
                <div className="success-icon">✅</div>
                <h3>공유 링크가 생성되었습니다!</h3>

                <div className="link-info">
                  <div className="info-row">
                    <span className="info-label">권한:</span>
                    <span className="info-value">
                      {permissionLabels[createdLink.permission].icon}{' '}
                      {permissionLabels[createdLink.permission].title}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">만료:</span>
                    <span className="info-value">{formatExpiryDate(createdLink.expires_at)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">사용 제한:</span>
                    <span className="info-value">
                      {createdLink.used_count} / {createdLink.max_uses || '∞'}
                    </span>
                  </div>
                </div>

                <div className="link-display">
                  <input
                    type="text"
                    value={createdLink.shareUrl}
                    readOnly
                    className="link-input"
                    onClick={(e) => e.target.select()}
                  />
                  <button className="copy-btn" onClick={handleCopyLink}>
                    📋 복사
                  </button>
                </div>

                <p className="link-hint">
                  💡 이 링크를 카카오톡이나 문자로 친구에게 전송하세요!
                </p>
              </div>

              <div className="modal-actions">
                <button className="btn-primary" onClick={onClose}>
                  완료
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareLinkModal;
